import { Auth } from 'aws-amplify'
import * as storage from './storage'

/**
 * Get current session with automatic token refresh
 * Handles expired tokens by attempting to refresh them automatically.
 * If refresh fails, clears storage and throws an error.
 * 
 * @returns {Promise<Object>} Session credentials with idToken, accessToken, refreshToken
 * @throws {Error} If session cannot be retrieved or refreshed
 */
export const getCurrentSession = async () => {
  try {
    // Try to get the current session (Amplify will auto-refresh if refresh token is valid)
    return await Auth.currentSession()
  } catch (error) {
    // If currentSession fails, try to refresh the session
    try {
      const user = await Auth.currentAuthenticatedUser()
      if (user) {
        // Force refresh the session
        return await Auth.currentSession({ forceRefresh: true })
      }
    } catch (refreshError) {
      // If refresh also fails, clear storage and throw error
      console.error('Session expired and refresh failed:', refreshError)
      clearSessionStorage()
      throw new Error('Session expired. Please log in again.')
    }
    throw error
  }
}

/**
 * Clear all session-related storage
 */
export const clearSessionStorage = () => {
  storage.del('token')
  storage.del('user')
  storage.del('role')
}

/**
 * Get authorization header with Bearer token
 * @returns {Promise<Object>} Headers object with Authorization header
 * @throws {Error} If session cannot be retrieved
 */
export const getAuthHeaders = async () => {
  const credentials = await getCurrentSession()
  return {
    Authorization: `Bearer ${credentials.idToken.jwtToken}`
  }
}

